// MODIFIED for dashboard integration:
//   - 2FA-setup redirect removed (dashboard owns 2FA enforcement now).
//   - Super-admin redirect to /super-administrator/tenants disabled
//     (dashboard owns top-level routing; CRM is one section among many).
//   - AuthGuard removed; CrmSessionBootstrap (in CrmRoot.tsx) is the
//     single auth gate and runs before this layout mounts.
//   - super-admin layout swap kept off — single CRMLayout for now.
//   - TopLoadingBar lifted to dashboard's AppShell so the entire
//     internal system shares one navigation loader (instead of the
//     CRM having its own copy that only fired inside /crm).

import { Box, CircularProgress } from "@mui/material"
import { CRMLayout } from "@crm-src/components/crm-layout"
import WebSocketProvider from "@crm-src/components/websocket-provider"
import { PageTransition } from "@crm-src/components/page-transition"
import { useLocation } from "react-router-dom";
import { Suspense } from "react"

function AuthenticatedLayoutContent({
  children,
}: {
  children: React.ReactNode
}) {
  const { pathname } = useLocation()

  const getCurrentPage = () => {
    // /crm lands on /crm/ai-prospects via the index <Navigate> (the CRM's
    // primary page), so the fallback only fires for transient renders before
    // the redirect kicks in. Use 'ai-prospects' so the sidebar's "AI Prospects"
    // entry stays highlighted during that brief window.
    // A release (`/crm/releases/10.2`) and a licence (`/crm/licences/7`) are
    // pages INSIDE their sidebar entry, so the entry stays lit while one is
    // open — the last segment would be "10.2", which lights nothing.
    const segments = pathname.split('/').filter(Boolean)
    const section = segments[1]
    if (section === 'releases' || section === 'licences') return section
    const path = segments[segments.length - 1] || 'ai-prospects'
    return path === '' ? 'ai-prospects' : path
  }

  return (
    <WebSocketProvider>
      <CRMLayout currentPage={getCurrentPage()}>
        <PageTransition>{children}</PageTransition>
      </CRMLayout>
    </WebSocketProvider>
  )
}

export default function AuthenticatedLayout({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <Suspense fallback={
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={32} sx={{ color: "primary.main" }} />
      </Box>
    }>
      <AuthenticatedLayoutContent>{children}</AuthenticatedLayoutContent>
    </Suspense>
  )
}
